import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueries } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from '@react-google-maps/api'
import {
  locationService,
  LiveTrackingDto,
  vehicleService,
  driverService,
  jobService,
  jobOfferService,
  VehicleDto,
  DriverDto,
  JobDto,
} from '../api/adminServices'
import { ErrorCard, EmptyState } from '../components/Feedback'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import {
  buildStopsFromJobStops,
  buildStopsFromShipments,
  buildStopMarkerDataUri,
  stopMarkerColor,
  type MapJobStop,
} from '../utils/mapJobStops'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '600px',
}

const defaultCenter = { lat: 31.7683, lng: 35.2137 } // Israel center
const defaultZoom = 8

type OnlineFilter = 'ALL' | 'ONLINE' | 'OFFLINE'

/** Unique key for a tracked entity — vehicleId may be null, so fall back to driverId. */
const keyOf = (v: LiveTrackingDto) => v.vehicleId || v.driverId

const ONLINE_MAX_AGE_MS = 3 * 60 * 1000

type NormalizedLiveTracking = LiveTrackingDto & {
  currentLatitude: number
  currentLongitude: number
  isOnline: boolean
}

type VehicleLookup = {
  plateNo?: string
}

type DriverLookup = {
  name?: string
}

function resolveVehicleIsOnline(snapshot: {
  isOnline?: boolean
  lastUpdateTime?: string
}): boolean {
  const updatedAt = snapshot.lastUpdateTime ? Date.parse(snapshot.lastUpdateTime) : NaN
  const isRecent = Number.isFinite(updatedAt) && Date.now() - updatedAt <= ONLINE_MAX_AGE_MS
  if (snapshot.isOnline === true) return true
  if (snapshot.isOnline === false) return isRecent
  return isRecent
}

function normalizeLiveTrackingRow(v: LiveTrackingDto): NormalizedLiveTracking | null {
  // Keep AdminConsole behavior aligned with Driver/Shipper live map rules:
  // vehicle must be known + coordinates must be finite and non-zero.
  if (!v.vehicleId) return null

  const lat = Number(v.currentLatitude)
  const lng = Number(v.currentLongitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) {
    return null
  }

  return {
    ...v,
    currentLatitude: lat,
    currentLongitude: lng,
    isOnline: resolveVehicleIsOnline(v),
  }
}

function normalizePagedData<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[]
  if (!payload || typeof payload !== 'object') return []
  const maybe = payload as { content?: unknown; data?: unknown }
  if (Array.isArray(maybe.content)) return maybe.content as T[]
  if (Array.isArray(maybe.data)) return maybe.data as T[]
  return []
}

function buildTruckMarkerDataUri(fill: string, opacity = 1): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34"><g opacity="${opacity}"><circle cx="17" cy="17" r="16" fill="${fill}"/><g fill="#fff"><rect x="7" y="12" width="14" height="8" rx="1.5"/><rect x="21" y="14" width="5" height="6" rx="1"/><circle cx="12" cy="22.5" r="2.2"/><circle cx="22" cy="22.5" r="2.2"/></g></g></svg>`
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

function normalizeJobStatus(status: unknown): string {
  return String(status ?? '').toUpperCase()
}

function jobStatusChipColor(
  status: string,
): 'error' | 'warning' | 'info' | 'success' | 'default' {
  const s = normalizeJobStatus(status)
  if (s === 'PROBLEM' || s === 'CANCELLED' || s === 'NO_SHOW') return 'error'
  if (s === 'IN_DELAY') return 'warning'
  if (s === 'IN_PROGRESS') return 'info'
  if (s === 'COMPLETED') return 'success'
  return 'default'
}

function resolveMarkerFill(
  isOnline: boolean,
  jobStatus: string | undefined,
  blinkOn: boolean,
): { fill: string; opacity: number } {
  if (normalizeJobStatus(jobStatus) === 'PROBLEM') {
    return { fill: '#D32F2F', opacity: blinkOn ? 1 : 0.2 }
  }
  return { fill: isOnline ? '#2E7D32' : '#9E9E9E', opacity: 1 }
}

function staleMinutes(v: LiveTrackingDto): number {
  if (!v.lastUpdateTime) return Infinity
  return Math.floor((Date.now() - new Date(v.lastUpdateTime).getTime()) / 60000)
}

export default function MapsPage() {
  const getLocalizedLabel = useLocalizedLabel()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [infoWindowKey, setInfoWindowKey] = useState<string | null>(null)
  const [selectedStopKey, setSelectedStopKey] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('ALL')
  const [problemBlinkOn, setProblemBlinkOn] = useState(true)
  const mapRef = useRef<google.maps.Map | null>(null)

  const { isLoaded: isMapsScriptLoaded, loadError: mapsLoadError } = useJsApiLoader({
    id: 'logargo-admin-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  })

  const markerScaledSize = useMemo(() => {
    if (!isMapsScriptLoaded || typeof google === 'undefined') return undefined
    return new google.maps.Size(34, 34)
  }, [isMapsScriptLoaded])

  const stopMarkerScaledSize = useMemo(() => {
    if (!isMapsScriptLoaded || typeof google === 'undefined') return undefined
    return new google.maps.Size(40, 40)
  }, [isMapsScriptLoaded])

  const {
    data: vehicles,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['all-vehicles-locations'],
    queryFn: () => locationService.getAllVehiclesLocations(),
    refetchInterval: 20000, // live refresh every 20s
  })

  const { data: vehiclesCatalog } = useQuery({
    queryKey: ['maps-vehicles-catalog'],
    queryFn: () => vehicleService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const { data: driversCatalog } = useQuery({
    queryKey: ['maps-drivers-catalog'],
    queryFn: () => driverService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const vehicleLookupById = useMemo(() => {
    const rows = normalizePagedData<VehicleDto>(vehiclesCatalog)
    const map = new Map<string, VehicleLookup>()
    rows.forEach((v) => {
      if (!v?.id) return
      map.set(v.id, { plateNo: v.plateNo })
    })
    return map
  }, [vehiclesCatalog])

  const driverLookupById = useMemo(() => {
    const rows = normalizePagedData<DriverDto>(driversCatalog)
    const map = new Map<string, DriverLookup>()
    rows.forEach((d) => {
      if (!d?.id) return
      map.set(d.id, {
        name: d.user?.name || d.user?.nickName || d.user?.phone || d.id,
      })
    })
    return map
  }, [driversCatalog])

  const allVehicles = useMemo(
    () => (vehicles ?? []).map(normalizeLiveTrackingRow).filter((v): v is NormalizedLiveTracking => !!v),
    [vehicles]
  )

  const trackedJobIds = useMemo(
    () => [...new Set(allVehicles.map((v) => v.jobId).filter((id): id is string => !!id))],
    [allVehicles],
  )

  const jobStatusQueries = useQueries({
    queries: trackedJobIds.map((jobId) => ({
      queryKey: ['maps-job-status', jobId],
      queryFn: () => jobService.getById(jobId),
      staleTime: 30_000,
    })),
  })

  const jobStatusById = useMemo(() => {
    const map = new Map<string, string>()
    trackedJobIds.forEach((jobId, index) => {
      const job = jobStatusQueries[index]?.data
      const status = normalizeJobStatus(job?.status)
      if (status) map.set(jobId, status)
    })
    return map
  }, [trackedJobIds, jobStatusQueries])

  const jobDetailsById = useMemo(() => {
    const map = new Map<string, JobDto>()
    trackedJobIds.forEach((jobId, index) => {
      const job = jobStatusQueries[index]?.data
      if (job?.id) map.set(jobId, job)
    })
    return map
  }, [trackedJobIds, jobStatusQueries])

  const hasProblemJobs = useMemo(
    () => allVehicles.some((v) => v.jobId && jobStatusById.get(v.jobId) === 'PROBLEM'),
    [allVehicles, jobStatusById],
  )

  useEffect(() => {
    if (!hasProblemJobs) {
      setProblemBlinkOn(true)
      return
    }
    const id = setInterval(() => setProblemBlinkOn((on) => !on), 550)
    return () => clearInterval(id)
  }, [hasProblemJobs])

  const filtered = useMemo(() => {
    return allVehicles.filter((v) => {
      if (onlineFilter === 'ONLINE' && !v.isOnline) return false
      if (onlineFilter === 'OFFLINE' && v.isOnline) return false
      if (search) {
        const q = search.toLowerCase()
        const plateNo = (v.vehicleId && vehicleLookupById.get(v.vehicleId)?.plateNo) || ''
        const driverName = (v.driverId && driverLookupById.get(v.driverId)?.name) || ''
        const hay = [v.vehicleId, v.driverId, v.jobId, plateNo, driverName]
        if (!hay.some((x) => x?.toLowerCase().includes(q))) return false
      }
      return true
    })
  }, [allVehicles, onlineFilter, search, vehicleLookupById, driverLookupById])

  const selected = useMemo(
    () => allVehicles.find((v) => keyOf(v) === selectedKey) || null,
    [allVehicles, selectedKey]
  )

  const selectedJobOfferId = useMemo(() => {
    if (!selectedKey || !selected?.jobId) return undefined
    return jobDetailsById.get(selected.jobId)?.jobOfferId
  }, [selectedKey, selected?.jobId, jobDetailsById])

  const { data: selectedJobOffer, isLoading: selectedOfferLoading } = useQuery({
    queryKey: ['maps-job-offer', selectedJobOfferId],
    queryFn: () => jobOfferService.getById(selectedJobOfferId!),
    enabled: !!selectedKey && !!selectedJobOfferId,
    staleTime: 60_000,
  })

  const needsShipmentFallback =
    !!selectedKey &&
    !!selectedJobOfferId &&
    !selectedOfferLoading &&
    (!Array.isArray(selectedJobOffer?.jobStops) || selectedJobOffer.jobStops.length === 0) &&
    (!Array.isArray(selectedJobOffer?.shipments) || selectedJobOffer.shipments.length === 0)

  const { data: selectedJobShipments, isLoading: selectedShipmentsLoading } = useQuery({
    queryKey: ['maps-job-shipments', selectedJobOfferId],
    queryFn: () => jobOfferService.getShipments(selectedJobOfferId!),
    enabled: needsShipmentFallback,
    staleTime: 60_000,
  })

  const selectedStopsLoading = selectedOfferLoading || (needsShipmentFallback && selectedShipmentsLoading)

  const selectedJobStops = useMemo(() => {
    if (!selectedKey) return []
    const jobStops = selectedJobOffer?.jobStops
    if (Array.isArray(jobStops) && jobStops.length > 0) {
      const fromJobStops = buildStopsFromJobStops(jobStops)
      if (fromJobStops.length > 0) return fromJobStops
    }
    const shipments =
      (Array.isArray(selectedJobOffer?.shipments) && selectedJobOffer.shipments.length > 0
        ? selectedJobOffer.shipments
        : selectedJobShipments) ?? []
    if (shipments.length > 0) {
      return buildStopsFromShipments(shipments)
    }
    return []
  }, [selectedKey, selectedJobOffer, selectedJobShipments])

  const selectedJobStatus = selected?.jobId ? jobStatusById.get(selected.jobId) : undefined
  const selectedPlateNo =
    (selected?.vehicleId && vehicleLookupById.get(selected.vehicleId)?.plateNo) ||
    selected?.vehicleId
  const selectedDriverName =
    (selected?.driverId && driverLookupById.get(selected.driverId)?.name) || selected?.driverId

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map
  }, [])
  const onMapUnmount = useCallback(() => {
    mapRef.current = null
  }, [])

  const fitBounds = useCallback((rows: LiveTrackingDto[]) => {
    const map = mapRef.current
    if (!map || rows.length === 0 || typeof google === 'undefined') return
    const bounds = new google.maps.LatLngBounds()
    rows.forEach((v) => {
      if (v.currentLatitude && v.currentLongitude) {
        bounds.extend({ lat: v.currentLatitude, lng: v.currentLongitude })
      }
    })
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds)
      map.setZoom(Math.min(map.getZoom() || defaultZoom, 12))
    }
  }, [])

  const fitBoundsToSelection = useCallback(
    (vehicle: NormalizedLiveTracking, stops: MapJobStop[]) => {
      const map = mapRef.current
      if (!map || typeof google === 'undefined') return
      const bounds = new google.maps.LatLngBounds()
      bounds.extend({ lat: vehicle.currentLatitude, lng: vehicle.currentLongitude })
      stops.forEach((stop) => {
        bounds.extend({ lat: stop.latitude, lng: stop.longitude })
      })
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48)
      }
    },
    [],
  )

  useEffect(() => {
    setSelectedStopKey(null)
    setInfoWindowKey(null)
  }, [selectedKey])

  // Auto-fit when the filtered set or selected vehicle stops change.
  useEffect(() => {
    if (!isMapsScriptLoaded) return
    const id = setTimeout(() => {
      if (selected && selectedJobStops.length > 0) {
        fitBoundsToSelection(selected, selectedJobStops)
      } else {
        fitBounds(filtered)
      }
    }, 150)
    return () => clearTimeout(id)
  }, [filtered, selected, selectedJobStops, fitBounds, fitBoundsToSelection, isMapsScriptLoaded])

  const focusVehicle = (v: LiveTrackingDto) => {
    setSelectedKey(keyOf(v))
    const map = mapRef.current
    if (map && v.currentLatitude && v.currentLongitude && !v.jobId) {
      map.panTo({ lat: v.currentLatitude, lng: v.currentLongitude })
      map.setZoom(Math.max(map.getZoom() || defaultZoom, 13))
    }
  }

  const onlineCount = allVehicles.filter((v) => v.isOnline).length
  const offlineCount = allVehicles.length - onlineCount

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box>
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="body1" color="error">
            Google Maps API key is not configured. Set VITE_GOOGLE_MAPS_API_KEY in your .env file.
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Online ${onlineCount}`} color="success" size="small" />
          <Chip label={`Offline ${offlineCount}`} size="small" />
          <Chip label={`Total ${allVehicles.length}`} color="primary" size="small" />
          {selectedKey && selectedStopsLoading && (
            <Chip label="Loading job stops…" size="small" variant="outlined" />
          )}
          {selectedKey && !selectedStopsLoading && selectedJobStops.length > 0 && (
            <Chip
              label={`${selectedJobStops.length} job stops`}
              size="small"
              color="info"
            />
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="600px">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Paper sx={{ mt: 2 }}>
          <ErrorCard
            title="Failed to load vehicle locations"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        </Paper>
      ) : (
        <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)', minHeight: 600 }}>
          {/* Side panel */}
          <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search vehicle / driver / job"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <ToggleButtonGroup
                  value={onlineFilter}
                  exclusive
                  size="small"
                  fullWidth
                  onChange={(_e, v) => v && setOnlineFilter(v)}
                  sx={{ mt: 1 }}
                >
                  <ToggleButton value="ALL">All</ToggleButton>
                  <ToggleButton value="ONLINE">Online</ToggleButton>
                  <ToggleButton value="OFFLINE">Offline</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {filtered.length === 0 ? (
                  <EmptyState
                    title="No vehicles"
                    description="No tracked vehicles match the current filter."
                  />
                ) : (
                  <List dense disablePadding>
                    {filtered.map((v) => {
                      const stale = staleMinutes(v)
                      const plateNo = (v.vehicleId && vehicleLookupById.get(v.vehicleId)?.plateNo) || v.vehicleId
                      const driverName = (v.driverId && driverLookupById.get(v.driverId)?.name) || v.driverId
                      const jobStatus = v.jobId ? jobStatusById.get(v.jobId) : undefined
                      const isProblemJob = jobStatus === 'PROBLEM'
                      return (
                        <ListItemButton
                          key={keyOf(v)}
                          selected={selectedKey === keyOf(v)}
                          onClick={() => focusVehicle(v)}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              mr: 1.5,
                              bgcolor: isProblemJob
                                ? 'error.main'
                                : v.isOnline
                                ? 'success.main'
                                : 'action.disabled',
                              opacity: isProblemJob && !problemBlinkOn ? 0.35 : 1,
                              transition: 'opacity 0.2s ease',
                            }}
                          />
                          <ListItemText
                            primary={
                              plateNo || 'Vehicle'
                            }
                            secondary={
                              `${driverName ? `Driver: ${driverName} · ` : ''}${v.jobId ? `job ${v.jobId.substring(0, 8)}… · ` : ''}${stale === Infinity ? 'no fix' : `${stale}m ago`}`
                            }
                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                            secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                          />
                          <Tooltip title="Focus on map">
                            <MyLocationIcon fontSize="small" color="action" />
                          </Tooltip>
                        </ListItemButton>
                      )
                    })}
                  </List>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Map */}
          <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
            <Paper sx={{ height: '100%', overflow: 'hidden' }}>
              {mapsLoadError ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body1" color="error">
                    Failed to load Google Maps. Check that your API key allows this site&apos;s URL
                    (HTTP referrer) and that Maps JavaScript API is enabled.
                  </Typography>
                </Box>
              ) : !isMapsScriptLoaded || !markerScaledSize ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: 600,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={defaultCenter}
                  zoom={defaultZoom}
                  onLoad={onMapLoad}
                  onUnmount={onMapUnmount}
                  options={{
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                  }}
                >
                  {filtered.map((v) => {
                    if (!v.currentLatitude || !v.currentLongitude) return null
                    const k = keyOf(v)
                    const plateNo = (v.vehicleId && vehicleLookupById.get(v.vehicleId)?.plateNo) || v.vehicleId
                    const driverName = (v.driverId && driverLookupById.get(v.driverId)?.name) || v.driverId
                    const jobStatus = v.jobId ? jobStatusById.get(v.jobId) : undefined
                    const markerStyle = resolveMarkerFill(v.isOnline, jobStatus, problemBlinkOn)
                    return (
                      <Marker
                        key={k}
                        position={{ lat: v.currentLatitude, lng: v.currentLongitude }}
                        icon={{
                          url: buildTruckMarkerDataUri(markerStyle.fill, markerStyle.opacity),
                          scaledSize: markerScaledSize,
                        }}
                        onClick={() => {
                          setSelectedKey(k)
                          setInfoWindowKey(k)
                        }}
                      >
                        {infoWindowKey === k && (
                          <InfoWindow onCloseClick={() => setInfoWindowKey(null)}>
                            <Box sx={{ p: 0.5, minWidth: 200 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {plateNo ? `Vehicle ${plateNo}` : 'Vehicle (unassigned)'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Driver {driverName || '—'}
                              </Typography>
                              {v.jobId && (
                                <Typography variant="body2" color="text.secondary">
                                  Job {v.jobId.substring(0, 8)}…
                                </Typography>
                              )}
                              <Box sx={{ my: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  label={v.isOnline ? 'Online' : 'Offline'}
                                  color={v.isOnline ? 'success' : 'default'}
                                  size="small"
                                  sx={{ height: 20 }}
                                />
                                {jobStatus && (
                                  <Chip
                                    label={getLocalizedLabel('jobStatus', jobStatus)}
                                    color={jobStatusChipColor(jobStatus)}
                                    size="small"
                                    sx={{ height: 20 }}
                                  />
                                )}
                              </Box>
                              {v.currentSpeed != null && (
                                <Typography variant="body2" color="text.secondary">
                                  Speed: {Number(v.currentSpeed).toFixed(1)} km/h
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" display="block">
                                Updated {new Date(v.lastUpdateTime).toLocaleString()}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ mt: 1 }}
                                onClick={() => {
                                  setInfoWindowKey(null)
                                  setDetailOpen(true)
                                }}
                              >
                                Details
                              </Button>
                            </Box>
                          </InfoWindow>
                        )}
                      </Marker>
                    )
                  })}
                  {selectedKey &&
                    stopMarkerScaledSize &&
                    selectedJobStops.map((stop) => (
                      <Marker
                        key={stop.key}
                        position={{ lat: stop.latitude, lng: stop.longitude }}
                        zIndex={200 + stop.sequence}
                        icon={{
                          url: buildStopMarkerDataUri(stopMarkerColor(stop.type), stop.sequence),
                          scaledSize: stopMarkerScaledSize,
                        }}
                        onClick={() => {
                          setSelectedStopKey(stop.key)
                          setInfoWindowKey(null)
                        }}
                      >
                        {selectedStopKey === stop.key && (
                          <InfoWindow onCloseClick={() => setSelectedStopKey(null)}>
                            <Box sx={{ p: 0.5, minWidth: 200 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Stop {stop.sequence}: {stop.label}
                              </Typography>
                              {stop.detail && stop.detail !== stop.label && (
                                <Typography variant="body2" color="text.secondary">
                                  {stop.detail}
                                </Typography>
                              )}
                              <Chip
                                label={stop.type === 'pickup' ? 'Pickup' : 'Delivery'}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  height: 20,
                                  bgcolor: stopMarkerColor(stop.type),
                                  color: '#fff',
                                }}
                              />
                            </Box>
                          </InfoWindow>
                        )}
                      </Marker>
                    ))}
                </GoogleMap>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Detail dialog */}
      <Dialog
        open={detailOpen && !!selected}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Vehicle Tracking Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Vehicle (plate)</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedPlateNo || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Driver</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedDriverName || '—'}
                </Typography>
              </Grid>
              {selected.jobId && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Job ID</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {selected.jobId}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Job status</Typography>
                <Box>
                  {selectedJobStatus ? (
                    <Chip
                      label={getLocalizedLabel('jobStatus', selectedJobStatus)}
                      color={jobStatusChipColor(selectedJobStatus)}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Latitude</Typography>
                <Typography variant="body2">{selected.currentLatitude?.toFixed(6) ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Longitude</Typography>
                <Typography variant="body2">{selected.currentLongitude?.toFixed(6) ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Speed</Typography>
                <Typography variant="body2">
                  {selected.currentSpeed != null ? `${Number(selected.currentSpeed).toFixed(1)} km/h` : '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Heading</Typography>
                <Typography variant="body2">
                  {selected.heading != null ? `${Number(selected.heading).toFixed(0)}°` : '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Battery</Typography>
                <Typography variant="body2">
                  {selected.batteryLevel != null ? `${selected.batteryLevel}%` : '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Network</Typography>
                <Typography variant="body2">{selected.networkType || '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box>
                  <Chip
                    label={selected.isOnline ? 'Online' : 'Offline'}
                    color={selected.isOnline ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Last update</Typography>
                <Typography variant="body2">
                  {new Date(selected.lastUpdateTime).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Button
                  size="small"
                  startIcon={<MyLocationIcon />}
                  onClick={() => {
                    setDetailOpen(false)
                    if (selected) focusVehicle(selected)
                  }}
                >
                  Center on map
                </Button>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
